import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Çerez Banner Bileşeni
export const CookieBanner = ({ lang }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setIsVisible(false);
    // Google Analytics'i etkinleştir
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleReject = () => {
    localStorage.setItem('cookie-consent', 'rejected');
    setIsVisible(false);
    // Google Analytics'i devre dışı bırak
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
    // GA çerezlerini sil
    document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = '_ga_FXMDR8BPQL=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  };

  if (!isVisible) return null;

  const text = {
    tr: {
      title: 'Çerez Kullanımı',
      message: 'Bu web sitesi, deneyiminizi geliştirmek ve site trafiğini analiz etmek için çerez kullanmaktadır. Detaylı bilgi için Çerez Politikamızı inceleyebilirsiniz.',
      policy: 'Çerez Politikası',
      reject: 'Reddet',
      accept: 'Kabul Et'
    },
    en: {
      title: 'Cookie Usage',
      message: 'This website uses cookies to improve your experience and analyze site traffic. For detailed information, please review our Cookie Policy.',
      policy: 'Cookie Policy',
      reject: 'Reject',
      accept: 'Accept'
    },
    es: {
      title: 'Uso de Cookies',
      message: 'Este sitio web utiliza cookies para mejorar su experiencia y analizar el tráfico del sitio. Para obtener información detallada, consulte nuestra Política de Cookies.',
      policy: 'Política de Cookies',
      reject: 'Rechazar',
      accept: 'Aceptar'
    }
  };

  const t = text[lang] || text.tr;

  return (
    <div className="cookie-banner" data-testid="cookie-banner">
      <div className="cookie-banner-content">
        <div className="cookie-banner-icon">🍪</div>
        <div className="cookie-banner-text">
          <h4>{t.title}</h4>
          <p>{t.message}</p>
        </div>
        <div className="cookie-banner-actions">
          <Link to="/cerez-politikasi" className="cookie-link">{t.policy}</Link>
          <button onClick={handleReject} className="cookie-btn cookie-btn-reject" data-testid="cookie-reject">
            {t.reject}
          </button>
          <button onClick={handleAccept} className="cookie-btn cookie-btn-accept" data-testid="cookie-accept">
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

// Çerez Politikası Sayfası
export const CookiePolicyPage = ({ lang, setLang }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    tr: {
      title: 'Çerez Politikası',
      lastUpdate: 'Son güncelleme: 08.04.2026',
      sections: [
        {
          title: '1. Çerez Nedir?',
          content: 'Çerezler (cookies), web sitesini ziyaret ettiğinizde cihazınıza kaydedilen küçük veri dosyalarıdır. Kullanıcı deneyimini geliştirmek ve siteyi daha verimli çalıştırmak amacıyla kullanılırlar.'
        },
        {
          title: '2. Kullanılan Çerez Türleri',
          content: `<ul>
            <li><strong>Zorunlu Çerezler:</strong> Web sitesinin düzgün çalışması için gereklidir.</li>
            <li><strong>Analitik Çerezler:</strong> Kullanıcı davranışlarını analiz ederek hizmet kalitesini artırmaya yardımcı olur.</li>
            <li><strong>Pazarlama Çerezleri:</strong> Kullanıcıya ilgi alanına uygun reklamlar sunmak için kullanılır (açık rıza ile).</li>
          </ul>`
        },
        {
          title: '3. Çerezlerin Kullanım Amaçları',
          content: `<ul>
            <li>Web sitesinin çalışmasını sağlamak</li>
            <li>Kullanıcı tercihlerini hatırlamak</li>
            <li>Trafik analizi yapmak</li>
            <li>Pazarlama faaliyetlerini yürütmek</li>
          </ul>`
        },
        {
          title: '4. Üçüncü Taraf Çerezler',
          content: `Web sitesi aşağıdaki üçüncü taraf hizmetlerini kullanabilir:
          <ul>
            <li>Google Analytics</li>
            <li>Ödeme sağlayıcıları</li>
          </ul>
          Bu hizmetler kendi gizlilik politikalarına tabidir.`
        },
        {
          title: '5. Çerez Yönetimi',
          content: 'Kullanıcılar, tarayıcı ayarları üzerinden çerezleri kontrol edebilir veya silebilir. Ayrıca, çerez kullanımına ilişkin tercihleri web sitesi üzerinden yönetilebilir.'
        },
        {
          title: '6. Hukuki Dayanak',
          content: `<ul>
            <li><strong>Zorunlu Çerezler:</strong> Meşru menfaat esasına dayanarak kullanılır.</li>
            <li><strong>Diğer Çerezler:</strong> Açık rıza esasına dayanarak kullanılır.</li>
          </ul>`
        },
        {
          title: '7. İletişim',
          content: 'Çerez politikamız hakkında sorularınız için <a href="mailto:info@novaiberian.com">info@novaiberian.com</a> adresinden bizimle iletişime geçebilirsiniz.'
        }
      ]
    },
    en: {
      title: 'Cookie Policy',
      lastUpdate: 'Last update: 08.04.2026',
      sections: [
        {
          title: '1. What are Cookies?',
          content: 'Cookies are small data files that are stored on your device when you visit a website. They are used to improve user experience and make the site work more efficiently.'
        },
        {
          title: '2. Types of Cookies Used',
          content: `<ul>
            <li><strong>Essential Cookies:</strong> Required for the proper functioning of the website.</li>
            <li><strong>Analytical Cookies:</strong> Help improve service quality by analyzing user behavior.</li>
            <li><strong>Marketing Cookies:</strong> Used to show ads relevant to user interests (with explicit consent).</li>
          </ul>`
        },
        {
          title: '3. Purpose of Cookie Usage',
          content: `<ul>
            <li>Ensure website functionality</li>
            <li>Remember user preferences</li>
            <li>Perform traffic analysis</li>
            <li>Conduct marketing activities</li>
          </ul>`
        },
        {
          title: '4. Third-Party Cookies',
          content: `The website may use the following third-party services:
          <ul>
            <li>Google Analytics</li>
            <li>Payment providers</li>
          </ul>
          These services are subject to their own privacy policies.`
        },
        {
          title: '5. Cookie Management',
          content: 'Users can control or delete cookies through browser settings. Cookie preferences can also be managed through the website.'
        },
        {
          title: '6. Legal Basis',
          content: `<ul>
            <li><strong>Essential Cookies:</strong> Used based on legitimate interest.</li>
            <li><strong>Other Cookies:</strong> Used based on explicit consent.</li>
          </ul>`
        },
        {
          title: '7. Contact',
          content: 'For questions about our cookie policy, please contact us at <a href="mailto:info@novaiberian.com">info@novaiberian.com</a>.'
        }
      ]
    },
    es: {
      title: 'Política de Cookies',
      lastUpdate: 'Última actualización: 08.04.2026',
      sections: [
        {
          title: '1. ¿Qué son las Cookies?',
          content: 'Las cookies son pequeños archivos de datos que se almacenan en su dispositivo cuando visita un sitio web. Se utilizan para mejorar la experiencia del usuario y hacer que el sitio funcione de manera más eficiente.'
        },
        {
          title: '2. Tipos de Cookies Utilizadas',
          content: `<ul>
            <li><strong>Cookies Esenciales:</strong> Necesarias para el correcto funcionamiento del sitio web.</li>
            <li><strong>Cookies Analíticas:</strong> Ayudan a mejorar la calidad del servicio analizando el comportamiento del usuario.</li>
            <li><strong>Cookies de Marketing:</strong> Se utilizan para mostrar anuncios relevantes para los intereses del usuario (con consentimiento explícito).</li>
          </ul>`
        },
        {
          title: '3. Propósito del Uso de Cookies',
          content: `<ul>
            <li>Garantizar la funcionalidad del sitio web</li>
            <li>Recordar las preferencias del usuario</li>
            <li>Realizar análisis de tráfico</li>
            <li>Llevar a cabo actividades de marketing</li>
          </ul>`
        },
        {
          title: '4. Cookies de Terceros',
          content: `El sitio web puede utilizar los siguientes servicios de terceros:
          <ul>
            <li>Google Analytics</li>
            <li>Proveedores de pago</li>
          </ul>
          Estos servicios están sujetos a sus propias políticas de privacidad.`
        },
        {
          title: '5. Gestión de Cookies',
          content: 'Los usuarios pueden controlar o eliminar las cookies a través de la configuración del navegador. Las preferencias de cookies también se pueden gestionar a través del sitio web.'
        },
        {
          title: '6. Base Legal',
          content: `<ul>
            <li><strong>Cookies Esenciales:</strong> Se utilizan en base al interés legítimo.</li>
            <li><strong>Otras Cookies:</strong> Se utilizan en base al consentimiento explícito.</li>
          </ul>`
        },
        {
          title: '7. Contacto',
          content: 'Para preguntas sobre nuestra política de cookies, contáctenos en <a href="mailto:info@novaiberian.com">info@novaiberian.com</a>.'
        }
      ]
    }
  };

  const t = content[lang] || content.tr;

  return (
    <div className="legal-page" data-testid="cookie-policy-page">
      <Helmet>
        <title>{t.title} | Nova Iberian</title>
        <meta name="description" content="Nova Iberian çerez politikası. Web sitemizde kullanılan çerezler hakkında bilgi." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://novaiberian.com/cerez-politikasi" />
      </Helmet>
      <div className="legal-page-header">
        <Link to="/" className="legal-back-link">
          ← {lang === 'tr' ? 'Ana Sayfa' : lang === 'en' ? 'Home' : 'Inicio'}
        </Link>
      </div>
      <div className="legal-page-content">
        <h1>{t.title}</h1>
        <p className="legal-update-date">{t.lastUpdate}</p>
        
        {t.sections.map((section, index) => (
          <div key={index} className="legal-section">
            <h2>{section.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: section.content }} />
          </div>
        ))}
        
        <div className="legal-company-info">
          <p><strong>Nova Iberian Consulting Group S.L.</strong></p>
          <p>Carrer de la Marina, 68, Bj 2</p>
          <p>Sant Martí, Barcelona 08005</p>
          <p>info@novaiberian.com</p>
        </div>
      </div>
    </div>
  );
};

// Aydınlatma Metni Sayfası
export const PrivacyPolicyPage = ({ lang, setLang }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    tr: {
      title: 'Aydınlatma Metni',
      subtitle: 'Kişisel Verilerin Korunması Hakkında Bilgilendirme',
      lastUpdate: 'Son güncelleme: 08.04.2026',
      sections: [
        {
          title: 'Veri Sorumlusu',
          content: `<p><strong>Nova Iberian Consulting Group S.L.</strong></p>
          <p>Adres: Carrer de la Marina, 68, Bj 2, Sant Martí, Barcelona 08005</p>
          <p>E-posta: info@novaiberian.com</p>`
        },
        {
          title: 'Toplanan Kişisel Veriler',
          content: `<ul>
            <li>Ad, soyad</li>
            <li>E-posta adresi</li>
            <li>Telefon numarası</li>
            <li>Fatura ve teslimat bilgileri</li>
            <li>IP adresi ve çerez verileri</li>
            <li>Kullanıcı işlem bilgileri</li>
          </ul>`
        },
        {
          title: 'Verilerin İşlenme Amaçları',
          content: `<ul>
            <li>Hizmetlerin sunulması ve sözleşmenin ifası</li>
            <li>Sipariş süreçlerinin yürütülmesi</li>
            <li>Müşteri desteği sağlanması</li>
            <li>Yasal yükümlülüklerin yerine getirilmesi</li>
            <li>Pazarlama ve iletişim faaliyetleri (açık rıza ile)</li>
          </ul>`
        },
        {
          title: 'Hukuki Dayanak',
          content: `<ul>
            <li><strong>Sözleşmenin ifası</strong> (GDPR Art. 6(1)(b))</li>
            <li><strong>Yasal yükümlülükler</strong> (GDPR Art. 6(1)(c))</li>
            <li><strong>Meşru menfaat</strong> (GDPR Art. 6(1)(f))</li>
            <li><strong>Açık rıza</strong> (GDPR Art. 6(1)(a))</li>
          </ul>`
        },
        {
          title: 'Verilerin Aktarımı',
          content: `Kişisel verileriniz aşağıdaki taraflarla paylaşılabilir:
          <ul>
            <li>Hizmet sağlayıcılar (hosting, ödeme sistemleri vb.)</li>
            <li>Yetkili kamu kurumları</li>
            <li>İşbirliği içinde olunan kuruluşlar</li>
          </ul>
          <p>Avrupa Ekonomik Alanı dışına aktarımda uygun koruma önlemleri uygulanır.</p>`
        },
        {
          title: 'Saklama Süresi',
          content: 'Kişisel verileriniz, işleme amacının gerektirdiği süre ve yasal yükümlülükler süresince saklanır, ardından silinir veya anonim hale getirilir.'
        },
        {
          title: 'GDPR Kapsamındaki Haklarınız',
          content: `<ul>
            <li>Verilerinize erişim hakkı</li>
            <li>Verilerin düzeltilmesini talep etme hakkı</li>
            <li>Verilerin silinmesini isteme hakkı ("unutulma hakkı")</li>
            <li>İşlemenin kısıtlanmasını talep etme hakkı</li>
            <li>Veri taşınabilirliği hakkı</li>
            <li>İşlemeye itiraz etme hakkı</li>
            <li>Denetim otoritesine şikayette bulunma hakkı</li>
          </ul>`
        },
        {
          title: 'KVKK Kapsamındaki Haklarınız (Türk Kullanıcılar)',
          content: `<ul>
            <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
            <li>Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme</li>
            <li>Kişisel verilerinizin düzeltilmesini talep etme</li>
            <li>Kişisel verilerinizin silinmesini veya yok edilmesini isteme</li>
            <li>Kişisel verilerinizin işlenmesine itiraz etme</li>
          </ul>`
        },
        {
          title: 'İletişim',
          content: 'Haklarınızı kullanmak veya sorularınız için <a href="mailto:info@novaiberian.com">info@novaiberian.com</a> adresinden bizimle iletişime geçebilirsiniz.'
        }
      ]
    },
    en: {
      title: 'Privacy Notice',
      subtitle: 'Information on Personal Data Protection',
      lastUpdate: 'Last update: 08.04.2026',
      sections: [
        {
          title: 'Data Controller',
          content: `<p><strong>Nova Iberian Consulting Group S.L.</strong></p>
          <p>Address: Carrer de la Marina, 68, Bj 2, Sant Martí, Barcelona 08005</p>
          <p>Email: info@novaiberian.com</p>`
        },
        {
          title: 'Personal Data Collected',
          content: `<ul>
            <li>Name, surname</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Billing and delivery information</li>
            <li>IP address and cookie data</li>
            <li>User transaction information</li>
          </ul>`
        },
        {
          title: 'Purposes of Data Processing',
          content: `<ul>
            <li>Provision of services and performance of contract</li>
            <li>Management of order processes</li>
            <li>Providing customer support</li>
            <li>Fulfilling legal obligations</li>
            <li>Marketing and communication activities (with explicit consent)</li>
          </ul>`
        },
        {
          title: 'Legal Basis',
          content: `<ul>
            <li><strong>Performance of contract</strong> (GDPR Art. 6(1)(b))</li>
            <li><strong>Legal obligations</strong> (GDPR Art. 6(1)(c))</li>
            <li><strong>Legitimate interest</strong> (GDPR Art. 6(1)(f))</li>
            <li><strong>Explicit consent</strong> (GDPR Art. 6(1)(a))</li>
          </ul>`
        },
        {
          title: 'Data Transfer',
          content: `Your personal data may be shared with:
          <ul>
            <li>Service providers (hosting, payment systems, etc.)</li>
            <li>Authorized public institutions</li>
            <li>Partner organizations</li>
          </ul>
          <p>Appropriate safeguards are applied for transfers outside the European Economic Area.</p>`
        },
        {
          title: 'Retention Period',
          content: 'Your personal data is retained for the duration required by the processing purpose and legal obligations, then deleted or anonymized.'
        },
        {
          title: 'Your Rights Under GDPR',
          content: `<ul>
            <li>Right to access your data</li>
            <li>Right to request rectification</li>
            <li>Right to request erasure ("right to be forgotten")</li>
            <li>Right to request restriction of processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Right to lodge a complaint with a supervisory authority</li>
          </ul>`
        },
        {
          title: 'Contact',
          content: 'To exercise your rights or for questions, please contact us at <a href="mailto:info@novaiberian.com">info@novaiberian.com</a>.'
        }
      ]
    },
    es: {
      title: 'Aviso de Privacidad',
      subtitle: 'Información sobre Protección de Datos Personales',
      lastUpdate: 'Última actualización: 08.04.2026',
      sections: [
        {
          title: 'Responsable del Tratamiento',
          content: `<p><strong>Nova Iberian Consulting Group S.L.</strong></p>
          <p>Dirección: Carrer de la Marina, 68, Bj 2, Sant Martí, Barcelona 08005</p>
          <p>Email: info@novaiberian.com</p>`
        },
        {
          title: 'Datos Personales Recopilados',
          content: `<ul>
            <li>Nombre, apellidos</li>
            <li>Dirección de correo electrónico</li>
            <li>Número de teléfono</li>
            <li>Información de facturación y entrega</li>
            <li>Dirección IP y datos de cookies</li>
            <li>Información de transacciones del usuario</li>
          </ul>`
        },
        {
          title: 'Finalidades del Tratamiento',
          content: `<ul>
            <li>Prestación de servicios y ejecución del contrato</li>
            <li>Gestión de procesos de pedidos</li>
            <li>Proporcionar soporte al cliente</li>
            <li>Cumplir con obligaciones legales</li>
            <li>Actividades de marketing y comunicación (con consentimiento explícito)</li>
          </ul>`
        },
        {
          title: 'Base Legal',
          content: `<ul>
            <li><strong>Ejecución del contrato</strong> (RGPD Art. 6(1)(b))</li>
            <li><strong>Obligaciones legales</strong> (RGPD Art. 6(1)(c))</li>
            <li><strong>Interés legítimo</strong> (RGPD Art. 6(1)(f))</li>
            <li><strong>Consentimiento explícito</strong> (RGPD Art. 6(1)(a))</li>
          </ul>`
        },
        {
          title: 'Transferencia de Datos',
          content: `Sus datos personales pueden compartirse con:
          <ul>
            <li>Proveedores de servicios (hosting, sistemas de pago, etc.)</li>
            <li>Instituciones públicas autorizadas</li>
            <li>Organizaciones asociadas</li>
          </ul>
          <p>Se aplican las salvaguardas adecuadas para transferencias fuera del Espacio Económico Europeo.</p>`
        },
        {
          title: 'Período de Retención',
          content: 'Sus datos personales se conservan durante el tiempo requerido por la finalidad del tratamiento y las obligaciones legales, luego se eliminan o anonimizan.'
        },
        {
          title: 'Sus Derechos bajo el RGPD',
          content: `<ul>
            <li>Derecho de acceso a sus datos</li>
            <li>Derecho de rectificación</li>
            <li>Derecho de supresión ("derecho al olvido")</li>
            <li>Derecho a la limitación del tratamiento</li>
            <li>Derecho a la portabilidad de datos</li>
            <li>Derecho de oposición</li>
            <li>Derecho a presentar una reclamación ante una autoridad de control</li>
          </ul>`
        },
        {
          title: 'Contacto',
          content: 'Para ejercer sus derechos o para consultas, contáctenos en <a href="mailto:info@novaiberian.com">info@novaiberian.com</a>.'
        }
      ]
    }
  };

  const t = content[lang] || content.tr;

  return (
    <div className="legal-page" data-testid="privacy-policy-page">
      <Helmet>
        <title>{t.title} | Nova Iberian</title>
        <meta name="description" content="Nova Iberian gizlilik politikası. Kişisel verilerinizin nasıl işlendiği hakkında bilgi." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://novaiberian.com/gizlilik-politikasi" />
      </Helmet>
      <div className="legal-page-header">
        <Link to="/" className="legal-back-link">
          ← {lang === 'tr' ? 'Ana Sayfa' : lang === 'en' ? 'Home' : 'Inicio'}
        </Link>
      </div>
      <div className="legal-page-content">
        <h1>{t.title}</h1>
        <p className="legal-subtitle">{t.subtitle}</p>
        <p className="legal-update-date">{t.lastUpdate}</p>
        
        {t.sections.map((section, index) => (
          <div key={index} className="legal-section">
            <h2>{section.title}</h2>
            <div dangerouslySetInnerHTML={{ __html: section.content }} />
          </div>
        ))}
        
        <div className="legal-company-info">
          <p><strong>Nova Iberian Consulting Group S.L.</strong></p>
          <p>Carrer de la Marina, 68, Bj 2</p>
          <p>Sant Martí, Barcelona 08005</p>
          <p>info@novaiberian.com</p>
        </div>
      </div>
    </div>
  );
};
